import { getTemplateNotificationSeenAction } from "actions/templateActions";
import StyledHeader from "components/designSystems/appsmith/StyledHeader";
import type { User } from "constants/userConstants";
import type { AppState } from "ee/reducers";
import { shouldShowLicenseBanner } from "ee/selectors/tenantSelectors";
import { Banner } from "ee/utils/licenseHelpers";
import EntitySearchBar from "pages/common/SearchBar/EntitySearchBar";
import { useEffect } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";
import { getCurrentUser } from "selectors/usersSelectors";
import styled from "styled-components";
import bootIntercom from "utils/bootIntercom";
import { useIsMobileDevice } from "utils/hooks/useDeviceDetect";

const StyledPageHeader = styled(StyledHeader)<{
  hideShadow?: boolean;
  isMobile?: boolean;
  showSeparator?: boolean;
  isBannerVisible?: boolean;
}>`
  justify-content: space-between;
  background: var(--ads-v2-color-bg);
  height: 48px;
  color: var(--ads-v2-color-bg);
  position: fixed;
  top: 0;
  z-index: var(--ads-v2-z-index-9);
  border-bottom: 1px solid var(--ads-v2-color-border);
  ${({ isMobile }) =>
    isMobile &&
    `
    padding: 0 12px;
    padding-left: 10px;
  `};
  ${({ isBannerVisible }) => isBannerVisible && `top: 40px;`};
`;

interface PageHeaderProps {
  user?: User;
  hideShadow?: boolean;
  showSeparator?: boolean;
  hideEditProfileLink?: boolean;
}

export function PageHeader(props: PageHeaderProps) {
  const { user } = props;
  const dispatch = useDispatch();

  const isMobile = useIsMobileDevice();
  useEffect(() => {
    dispatch(getTemplateNotificationSeenAction());
  }, []);

  useEffect(() => {
    bootIntercom(user);
  }, [user?.email]);

  const showBanner = useSelector(shouldShowLicenseBanner);
  const matchApplications = useRouteMatch("/applications/*");
  const matchHome = useRouteMatch("/home");
  const matchApplicationsExact = useRouteMatch("/applications");
  const matchModules = useRouteMatch("/modules");
  const matchDataModeling = useRouteMatch("/data-modeling");
  const isDataModelingPage = useRouteMatch("/data-model-editor");
  const matchWorkflowApproval = useRouteMatch("/workflow-approval");
  const matchApprovalProcess = useRouteMatch("/af-*");
  const matchTemplates = useRouteMatch("/new-templates");
  const matchAgent = useRouteMatch("/agent");
  const matchWorkflow = useRouteMatch("/workflow");
  const isHomePage =
    matchApplications?.isExact ||
    matchHome?.isExact ||
    matchApplicationsExact?.isExact ||
    matchModules?.isExact ||
    matchDataModeling?.isExact ||
    isDataModelingPage?.isExact ||
    matchWorkflowApproval?.isExact ||
    matchApprovalProcess?.isExact ||
    matchTemplates?.isExact ||
    matchAgent?.isExact ||
    matchWorkflow?.isExact;

  const isLicensePage = useRouteMatch("/license")?.isExact;
  // const isModulesPage = useRouteMatch("/modules")?.isExact;
  return (
    <>
      <Banner />
      {!isHomePage && (
        <StyledPageHeader
          data-testid="t--appsmith-page-header"
          hideShadow={props.hideShadow || false}
          isBannerVisible={showBanner && isLicensePage}
          isMobile={isMobile}
          showSeparator={props.showSeparator || false}
        >
          <EntitySearchBar user={user} />
        </StyledPageHeader>
      )}
    </>
  );
}

const mapStateToProps = (state: AppState) => ({
  user: getCurrentUser(state),
  hideShadow: state.ui.theme.hideHeaderShadow,
  showSeparator: state.ui.theme.showHeaderSeparator,
});

export default connect(mapStateToProps)(PageHeader);
